import * as ReqType from '../types/ServerRequests';
import LoginServices from './LoginServices';
import Axios, { AxiosResponse } from 'axios';
import { UserProfileService } from './UserProfileService';
import { MediaService } from './MediaService';
import { UserRole } from '../types/UserRole';

/**
 * Cette classe permet la communication entre le backend et le frontend
 * Elle assure l'envois la reception des données entre le backend et frontend
 * Elle permet la creation, modification, et suppression des Utilisateurs
 */
export class UserService {
    private static instance: UserService = new UserService();

    public static getInstance(): UserService {
        return this.instance;
    }

    /**
     * retourne la liste des utilisateurs
     * @returns {Promise<UserView[]>}
     */
    public getUsers(): Promise<ReqType.UserView[]> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_USER_LIST_URL!,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.UserView[]>) => {
                return response.data;
            }
        ));
    }

    /**
     * retourne un utilisateur a partir d'un id
     * @param {number} id
     * @returns {Promise<UserView>}
     */
    public getUser(id: number): Promise<ReqType.UserView> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_PROFILE_URL! + '&id=' + id,
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            (response: AxiosResponse<ReqType.UserView>) => {
                return response.data;
            }
        ));
    }

    /**
     * retourne un utilisateur a partir un id
     * @param {UserView} user
     * @returns {Promise<void>}
     */
    public deleteUser(user: ReqType.UserView): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.delete(
            process.env.REACT_APP_USER_DELETE_URL! + '&id=' + user.id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            () => {
                return;
            }
        ));
    }

    /**
     * l'edition d'un utilisateur
     * @param {UserView} newUser
     * @param {UserView} oldUser
     * @returns {Promise<UserView>}
     */
    public editUser(newUser: ReqType.UserView, oldUser: ReqType.UserView): Promise<ReqType.UserView> {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Promise.all([
                this.getUserProfilePromise(newUser, oldUser)
            ]).then(
                (array) => {
                    var userProfile: ReqType.ProfileInfo | null = array[0];
                    var monUser = {
                        id: newUser.id,
                        status: newUser.status,
                        superadmin: newUser.superadmin,
                        username: newUser.username,
                        userProfile_id: userProfile !== null ? userProfile.id : null,
                        email: newUser.email,
                    };
                    return (Axios.put(
                        process.env.REACT_APP_USER_UPDATE_URL! + '&id=' + oldUser.id,
                        monUser,
                        {
                            headers: { 'X-Access-Token': token },
                        }
                    ).then(
                        (response) => {
                            newUser.userProfile = userProfile;
                            return newUser;
                        }
                    ));
                }
            )
        );
    }

    /**
     * creer une replique de l'utlisateur passer en parametre
     * @param {UserView} origin
     * @returns {UserView}
     */
    public duplicateUserView(origin: ReqType.UserView): ReqType.UserView {
        return JSON.parse(JSON.stringify(origin));
    }

    /**
     * retourne les informations financières d'un utilisateur apartir de son id
     * @param {Number} id
     * @returns {Promise<Frais[]>}
     */
    public getUserFees(id: Number): Promise<ReqType.Frais[]> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_USER_FEES_URL! + '&id=' + id,
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            (response: AxiosResponse<ReqType.Frais[]>) => {
                return response.data;
            }
        ));
    }

    /**
     * retourne les cours d'un utilisateur à partir de son id
     * @param {Number} id
     * @returns {Promise<Course[]>}
     */
    public getUserCourses(id: Number): Promise<ReqType.Course[]> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_USER_COURSES_URL! + '&id=' + id,
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            (response: AxiosResponse<ReqType.Course[]>) => {
                return response.data;
            }
        ));
    }

    /**
     * retourne les documents d'un utilisateur à partir de son id
     * @param {string} id
     * @returns {Promise<Fichiers[]>}
     */
    public getDocuments(id: string): Promise<ReqType.Fichiers[]> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_USER_FILES_URL! + '&id=' + id,
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            (response: AxiosResponse) => {
                const fileArr: ReqType.Fichiers[] = response.data.map(
                    (data: {
                        created_at: number,
                        id: number,
                        lien: string,
                        nom: string,
                        type_fichier_id: number,
                        user_id: number
                    }) => {
                        var retVal: ReqType.Fichiers = {
                            created_at: data.created_at,
                            id: data.id,
                            lien: MediaService.getInstance().getBaseURL() +
                                data.lien,
                            nom: data.nom,
                            type_file: data.type_fichier_id,
                            user_id: data.user_id,
                        };
                        return retVal;
                    }
                );
                return (fileArr);
            }
        ));
    }

    /**
     * modification de mot de passe d'un utilisateur
     * @param {string} oldP
     * @param {string} newP
     * @returns {Promise<void>}
     */
    public changeOwnPassword(oldP: string, newP: string): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        const id = LoginServices.getInstance().getUserId();
        return (Axios.post(
            process.env.REACT_APP_USER_CHANGE_OWN_PASSWORD_URL! + '&id=' + id,
            {
                oldpassword: oldP,
                newpassword: newP,
            },
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            () => { return; }
        ));
    }

    /**
     * changement de mot de passe d'un utilisateur
     * @param {number} userId
     * @param {string} newP
     * @returns {Promise<void>}
     */
    public changeUserPassword(userId: number, newP: string): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.post(
            process.env.REACT_APP_USER_CHANGE_USER_PASSWORD_URL! + '&id=' + userId,
            {
                newpassword: newP,
            },
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            () => { return; }
        ));
    }

    /**
     * la promotion d'un utilisateur
     * @param {number} userId
     * @param {UserRole} role
     * @returns {Promise<void>}
     */
    public promoteUser(userId: number, role: UserRole): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return (Axios.get(
            process.env.REACT_APP_USER_PROMOTE_URL! + '&id=' + userId + '&type=' + role,
            {
                headers: {
                    'X-Access-Token': token
                },
            }
        ).then(
            () => { return; }
        ));
    }

    /**
     * association d'un tag a un utilisateur
     * @param {number} userId
     * @param {number} tagId
     * @returns {Promise<void>}
     */
    public addTag(userId: number, tagId: number): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_USER_ADD_TAG_URL! + '&userid=' + userId + '&tagid=' + tagId,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            () => {
                return;
            }
        );
    }

    /**
     * supression d'un tag sur un utilisateur
     * @param {number} userId
     * @param {number} tagId
     * @returns {Promise<void>}
     */
    public removeTag(userId: number, tagId: number): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.delete(
            process.env.REACT_APP_USER_REMOVE_TAG_URL! + '&userid=' + userId + '&tagid=' + tagId,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            () => {
                return;
            }
        );
    }

    /**
     * retourne le profil d'un utilisateur
     * @param {UserView} newUser
     * @param {UserView} oldUser
     * @returns {Promise<ProfileInfo> | Promise<null>}
     */
    private getUserProfilePromise(
        newUser: ReqType.UserView,
        oldUser: ReqType.UserView
    ): Promise<ReqType.ProfileInfo> | Promise<null> {
        if (newUser.userProfile === null) {
            return Promise.resolve(null);
        } else if (oldUser.userProfile === null) {
            return (UserProfileService.getInstance().createProfil(newUser.userProfile));
        } else {
            return (UserProfileService.getInstance().updateProfil(newUser.userProfile, oldUser.userProfile));
        }
    }
}


// WEBPACK FOOTER //
// ./src/services/UserService.tsx